import dayjs, { Dayjs } from "dayjs";
import { CalendarSheet } from "./interface";

const DATE_ISO = "YYYY-MM-DD";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const getDayNames = (
    locale = "en",
    format: "long" | "short" | "narrow" = "long",
    firstWeekDay: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 0
): string[] => {
    const formatter = new Intl.DateTimeFormat(locale, {
        weekday: format,
        timeZone: "UTC",
    });
    const days = [1, 2, 3, 4, 5, 6, 7].map((day) => {
        const dd = day < 10 ? `0${day}` : day;
        return new Date(`2017-01-${dd}T00:00:00+00:00`);
    });
    return days
        .map((_date, index) => days[(index + firstWeekDay) % 7])
        .map((date) => formatter.format(date));
};

export const getMonthNames = (
    locale: string | string[] | undefined,
    format: "long" | "short" | "narrow" = "long"
): string[] => {
    const formatter = new Intl.DateTimeFormat(locale, {
        month: format,
        timeZone: "UTC",
    });
    const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
        const mm = month < 10 ? `0${month}` : month;
        return new Date(`2017-${mm}-01T00:00:00+00:00`);
    });
    return months.map((date) => formatter.format(date));
};

const getCalendarSheetStartDate = (
    year: number,
    month: number,
    firstWeekday: number = 1
): Dayjs => {
    const firstWeekdayFixed = firstWeekday;
    const firstDayOfMonth = dayjs().year(year).month(month).date(1);
    const startDate = firstDayOfMonth.clone().day(firstWeekdayFixed);
    return startDate.valueOf() > firstDayOfMonth.valueOf()
        ? startDate.subtract(7, "day")
        : startDate;
};

const getCalendarSheetEndDate = (
    year: number,
    month: number,
    firstWeekday: number = 1
): Dayjs => {
    const lastWeekday = (firstWeekday + 6) % 7;
    let lastDayOfMonth = dayjs().year(year).month(month).date(1);
    lastDayOfMonth = lastDayOfMonth.date(lastDayOfMonth.daysInMonth());
    const endDate = lastDayOfMonth.clone().day(lastWeekday);
    return endDate.valueOf() < lastDayOfMonth.valueOf()
        ? endDate.add(7, "day")
        : endDate;
};

export const getCalendarSheet = (
    year: number,
    month: number,
    firstWeekday: number = 1,
    language?: string
): CalendarSheet => {
    if (typeof year !== "number" || typeof month !== "number") {
        return;
    }
    const lng = language !== undefined && language.length >= 2 ? language : "";
    const startDate = dayjs(
        getCalendarSheetStartDate(year, month, firstWeekday).format(DATE_ISO)
    );
    const endDate = dayjs(
        getCalendarSheetEndDate(year, month, firstWeekday).format(DATE_ISO)
    );
    const numDays = Math.round(
        (endDate.valueOf() - startDate.valueOf()) / MS_PER_DAY
    );
    const numDaysTotal = 7 * Math.ceil((numDays + 1) / 7);

    const days = [];
    for (let i = 0; i < numDaysTotal; i++) {
        days.push(startDate.add(i, "day").format(DATE_ISO));
    }

    const monthName = (lng !== ""
        ? dayjs().month(month).locale(language)
        : dayjs().month(month)
    ).format("MMM");
    const dayNames: string[] = days.slice(0, 7).map((date) => {
        return (lng !== "" ? dayjs(date).locale(lng) : dayjs(date)).format(
            "dd"
        );
    });

    return {
        year,
        month,
        monthName,
        days,
        dayNames,
    };
};
